import normalizeEpisodeData from '../utils/normalizeEpisodeData'
import {
  SET_USER_DATA,
  SET_API_ERROR,
  CLEAR_API_ERROR,
  CLEAR_USER_DATA,
  PLAYER_API_REQUEST,
  PLAYER_API_RECEIVE,
  SET_GUIDANCE_ACCEPTED,
  SET_STOP_RESUME_POSITION,
  CLEAR_STOP_RESUME_POSITION,
  STOP_RESUME_START_FROM_BEGINNING,
  STOP_RESUME_START_FROM_SR_POSITION,
  SET_PIN_ACCEPTED,
  SET_PIN,
  FETCH_LOGIN_ENABLED_SUCCESS,
  FETCH_LOGIN_ENABLED_FAILURE,
  SET_PLAYER_ERROR,
  SET_COOKIE_POLICY_ACCEPTED,
  SET_TOGGLE_STATE,
  SET_HIDE_SPONSOR,
  CLEAR_PIN,
  SET_CHANNELS,
  FETCH_PS_ENABLED_SUCCESS,
  FETCH_PS_ENABLED_FAILURE,
  SET_EPISODE_DATA,
  SET_NEXT_EPISODE_DATA,
  SET_RECOMMENDED_PROGRAMME,
  SET_CURRENT_EPISODE,
} from '../constants'
import { setFeatureFlagCookie, getFeatureFlagsFromCookie } from '../utils/featureFlags'

export const setUserData = payload => ({
  payload,
  type: SET_USER_DATA,
})

export const clearUserData = () => ({
  type: CLEAR_USER_DATA,
})

export const playerApiRequest = path => ({
  path,
  type: PLAYER_API_REQUEST,
})

export const playerApiReceive = ({ path, response }) => ({
  response,
  path,
  type: PLAYER_API_RECEIVE,
})

export const setGuidanceAccepted = path => ({
  type: SET_GUIDANCE_ACCEPTED,
  path,
})
export const setPin = ({ encodedPin }) => ({
  encodedPin,
  type: SET_PIN,
})

export const setPinAccepted = ({ path }) => ({
  path,
  type: SET_PIN_ACCEPTED,
})

export const clearPinAccepted = pin => ({
  pin,
  type: CLEAR_PIN,
})

export const setCookiePolicyAccepted = ({ cookieSeen }) => ({
  cookieSeen,
  type: SET_COOKIE_POLICY_ACCEPTED,
})

export const setStopResumePosition = ({ id, path, position }) => ({
  type: SET_STOP_RESUME_POSITION,
  id,
  path,
  position,
})

export const clearStopResumePosition = ({ id }) => ({
  type: CLEAR_STOP_RESUME_POSITION,
  id,
})

export const resumeEpisode = ({
  path,
}) => ({
  path,
  type: STOP_RESUME_START_FROM_SR_POSITION,
})

export const startEpisode = ({
  path,
}) => ({
  path,
  type: STOP_RESUME_START_FROM_BEGINNING,
})

export const toggleSearch = () => ({
  type: 'TOGGLE_SEARCH',
})

export const closeSearch = () => ({
  type: 'CLOSE_SEARCH',
})

export const openSearch = () => ({
  type: 'OPEN_SEARCH',
})

export const setToggleState = activeState => ({
  activeState,
  type: SET_TOGGLE_STATE,
})

export const setHideSponsor = hideSponsor => ({
  hideSponsor,
  type: SET_HIDE_SPONSOR,
})

export const setChannels = channels => ({
  type: SET_CHANNELS,
  payload: channels,
})

export const fetchLoginEnabledSuccess = loginEnabled => ({
  loginEnabled,
  type: FETCH_LOGIN_ENABLED_SUCCESS,
})

export const fetchLoginEnabledFailure = error => ({
  error,
  type: FETCH_LOGIN_ENABLED_FAILURE,
})

export function fetchLoginEnabled(playerApi) {
  return async (dispatch, getState) => {
    const cachedRecord = getState().playerApiCache['/settings/playerV3DesktopLogIn']

    function onSuccess(loginEnabled) {
      dispatch(fetchLoginEnabledSuccess(loginEnabled))
      return loginEnabled
    }

    function onError(error) {
      dispatch(fetchLoginEnabledFailure(error))
      return error
    }

    if (typeof cachedRecord === 'undefined') {
      try {
        const { results: loginEnabledNumber, reason } = await playerApi({
          path: '/settings/playerV3DesktopLogIn',
          query: { groupToken: '0071' },
          dispatch,
        })
        if (reason) {
          return onError(reason)
        }
        const loginEnabled = Boolean(Number(loginEnabledNumber))
        return onSuccess(loginEnabled)
      } catch (error) {
        return onError(error)
      }
    }
    return null
  }
}

export const fetchPsEnabledSuccess = psEnabled => ({
  psEnabled,
  type: FETCH_PS_ENABLED_SUCCESS,
})
export const setCurrentEpisode = episodeData => ({
  type: SET_CURRENT_EPISODE,
  payload: episodeData,
})

export const fetchPsEnabledFailure = error => ({
  error,
  type: FETCH_PS_ENABLED_FAILURE,
})

// This could be renamed and refactored slightly to be more generic and handle many feature flags
export function fetchPsEnabled(ctx, playerApi) {
  return async (dispatch) => {
    // First check URL param and cookies
    try {
      const tokenParamBase64 = ctx?.query?.token
      const tokenParam = tokenParamBase64 ? atob(tokenParamBase64) : null
      const tokenParamJson = tokenParam ? JSON.parse(tokenParam) : {}
      const existingFlags = getFeatureFlagsFromCookie(ctx)

      // Check if token is present and sets psEnabled
      const tokenPresent = tokenParamBase64 && tokenParamJson.psEnabled === true

      // If token sets psEnabled, set the cookie
      if (tokenPresent) {
        const newFlags = {
          ...existingFlags,
          psEnabled: true,
        }
        setFeatureFlagCookie(newFlags, ctx)
      }

      // If token present or existing cookie, set redux state and return
      if (tokenPresent || existingFlags.psEnabled) {
        dispatch(fetchPsEnabledSuccess(true))
        return true
      }

      // If no token or cookie override, check settings API
      try {
        const { results: psEnabledFromApi } = await playerApi({
          path: '/settings/playerWebPremierSportEnabled',
          dispatch,
        })
        const apiResult = psEnabledFromApi === '1'

        // Update Redux state with API result
        dispatch(fetchPsEnabledSuccess(apiResult))

        return apiResult
      } catch (apiError) {
        // Fall back to existing value if API fails
        dispatch(fetchPsEnabledSuccess(false))
        return false
      }
    } catch (error) {
      dispatch(fetchPsEnabledFailure(error))
      return false // Default to false on any unexpected error
    }
  }
}

export const setPlayerError = error => ({
  error,
  type: SET_PLAYER_ERROR,
})
export const setApiError = apiError => ({
  apiError,
  type: SET_API_ERROR,
})

export const clearApiError = () => ({
  type: CLEAR_API_ERROR,
})

export const updateEpisodeData = (episodeData) => {
  const normalizedEpisodeData = normalizeEpisodeData(episodeData)
  return ({
    type: SET_EPISODE_DATA,
    payload: normalizedEpisodeData,
  })
}

export const setNextEpisodeData = nextEpisodeData => ({
  type: SET_NEXT_EPISODE_DATA,
  payload: nextEpisodeData,
})

export const fetchNextEpisodeData = (currentEpisodeGuid, playerApi) => async (dispatch) => {
  try {
    const recommendedResponse = await playerApi({
      path: `/recommended/${currentEpisodeGuid}`,
      dispatch,
    })

    // Access the next and promoted properties from the results object
    const nextEpisode = recommendedResponse.results?.next
    const promotedProgramme = recommendedResponse.results?.promoted

    if (nextEpisode && nextEpisode.type === 'episode') {
      const episodeResponse = await playerApi({
        path: `/episodes/${nextEpisode.guid}`,
        dispatch,
      })

      if (episodeResponse.results) {
        dispatch({
          type: SET_NEXT_EPISODE_DATA,
          payload: episodeResponse.results,
        })
      }
    } else if (promotedProgramme) {
      try {
        // Fetch the full programme data for the promoted programme
        const fullProgrammeResponse = await playerApi({
          path: `/programmes/${promotedProgramme.guid}`,
          query: { groupToken: '0071' }, // Use a default group token
          dispatch,
        })

        // Dispatch the full programme data to Redux
        if (fullProgrammeResponse.results) {
          dispatch({
            type: SET_RECOMMENDED_PROGRAMME,
            payload: fullProgrammeResponse.results, // Store the full object
          })
        } else {
          // Fallback to the limited data if the full fetch fails
          dispatch({
            type: SET_RECOMMENDED_PROGRAMME,
            payload: promotedProgramme,
          })
        }
        const episodeParams = {
          limit: 1,
          offset: 0,
          orderBy: 'number',
          'programme.guid': promotedProgramme.guid,
          groupToken: '0071',
        }

        // Fetch and set the first episode of the promoted programme
        const episodesResponse = await playerApi({
          path: '/episodes',
          query: episodeParams,
          dispatch,
        })

        if (episodesResponse.results?.[0]) {
          dispatch({
            type: SET_NEXT_EPISODE_DATA,
            payload: episodesResponse.results[0],
          })
        } else {
          dispatch({
            type: SET_NEXT_EPISODE_DATA,
            payload: null,
          })
        }
      } catch (programmeError) {
        dispatch({
          type: SET_NEXT_EPISODE_DATA,
          payload: null,
        })
      }
    } else {
      // Clear both next episode and recommended programme if neither is available
      dispatch({
        type: SET_NEXT_EPISODE_DATA,
        payload: null,
      })
      dispatch({
        type: SET_RECOMMENDED_PROGRAMME,
        payload: null,
      })
    }
  } catch (error) {
    // Clear the next episode data and recommended programme in case of an error
    dispatch({
      type: SET_NEXT_EPISODE_DATA,
      payload: null,
    })
    dispatch({
      type: SET_RECOMMENDED_PROGRAMME,
      payload: null,
    })
    throw error
  }
}
